import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { FormDrawer } from '../../../../../components/ui';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import {
  createEmptyTrigger,
  hydrateTriggerForEdit,
  validateTriggerDraft,
} from '../../utils/triggerFormUtils';
import TriggerTypeFields from './TriggerTypeFields';
import TriggerTypeSelect from './TriggerTypeSelect';

const DEFAULT_NEW_TRIGGER_TYPE = 'assignLead';

function resolveDrawerDraft(open, isEditMode, trigger) {
  if (!open) return null;
  if (isEditMode) {
    return trigger ? hydrateTriggerForEdit(trigger) : null;
  }
  return createEmptyTrigger(DEFAULT_NEW_TRIGGER_TYPE);
}

export default function TriggerEditorDrawer({
  open,
  contextLabel = '',
  trigger,
  triggerIndex = null,
  workspaceId,
  onClose,
  onSave,
  isSaving = false,
}) {
  const copy = FORM_STUDIO_COPY.automations.statusTriggers;
  const isEditMode = triggerIndex != null;
  const seedDraft = useMemo(
    () => resolveDrawerDraft(open, isEditMode, trigger),
    [open, isEditMode, trigger]
  );
  const [draft, setDraft] = useState(seedDraft);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setDraft(null);
      setErrors({});
      return;
    }
    setDraft(resolveDrawerDraft(open, isEditMode, trigger));
    setErrors({});
  }, [open, isEditMode, trigger]);

  const handleSubmit = () => {
    if (!draft) return;
    const nextErrors = validateTriggerDraft(draft);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onSave?.(draft, triggerIndex);
  };

  const handleTypeChange = (nextType) => {
    setDraft(createEmptyTrigger(nextType));
    setErrors({});
  };

  const activeDraft = draft ?? seedDraft;

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={isEditMode ? copy.editTrigger : copy.addTrigger}
      subtitle={contextLabel}
      submitLabel="Apply"
      onSubmit={handleSubmit}
      isSubmitting={isSaving}
    >
      {activeDraft ? (
        <Stack spacing={2}>
          <TriggerTypeSelect
            value={activeDraft.trigger_type}
            onChange={handleTypeChange}
            error={errors.trigger_type}
          />
          <TriggerTypeFields
            key={`${isEditMode ? triggerIndex : 'new'}-${activeDraft.trigger_type}`}
            trigger={activeDraft}
            onChange={setDraft}
            errors={errors}
            workspaceId={workspaceId}
          />
          {Object.keys(errors).length > 0 ? (
            <Typography variant="caption" color="error">
              {copy.validationHint}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </FormDrawer>
  );
}
